import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDatabase, ref, onValue, get } from 'firebase/database';
import { auth } from '../firebase';

export interface UserData {
  uid: string;
  email: string;
  username: string;
  userIcon: string | null;
  schoolName: string;
  schoolID: string;
  // Additional ProfileData fields
  city: string;
  classYear: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  primaryLanguage: string;
  province: string;
  status: string;
  summary: string;
  gender: string;
  hsBoard: string;
  registerStep: string;
  isInternational: boolean;
  country: string;
  userType?: number; // 1=student, 2=parent, 3=guidance, 4=other
  isGuidanceUser?: boolean; // True if user is a guidance counselor
  selfClaimedGuidance?: boolean; // True if user has certified they are in a Guidance role
  // Default student filter for guidance users
  defaultStudentFilter?: {
    lastNameRange?: { start: string; end: string }; // 1-3 characters each
    grades?: string[];
  };
  // Social media URLs
  instagramUrl: string;
  linkedinUrl: string;
  twitterUrl: string;
  // account is the same as uid
  account: string;
  // Timestamp for when user data was last updated
  lastUpdated?: number;
  // Course data from ProfileData
  courses: {
    [courseCode: string]: {
      grade: string;
      grades: number;
      gradeType?: string;
    };
  };
  // Grade averages from gradesAVG
  grade12Average?: number;
  grade12Top6Average?: number;
  // ProfileExtra data for additional profile sections
  profileExtra?: {
    work_VolunteerExpProfile?: Record<string, {
      position?: string;
      employer?: string;
      startDate?: string;
      endDate?: string;
      description?: string;
    }>;
    athleticsSportsProfile?: Record<string, {
      sport?: string;
      position?: string;
      startGrade?: string;
      endGrade?: string;
      description?: string;
      jerseyNumber?: string;
      level?: string;
      youtubeLink?: string;
      articleLink?: string;
      isContinue?: boolean;
    }>;
    academicAwardsHonoursProfile?: Record<string, {
      title?: string;
      grade?: string;
      yearReceived?: string;
      description?: string;
    }>;
    academicExtenuatingCircumstancesProfile?: Record<string, {
      description?: string;
    }>;
    interests_ActivitiesProfile?: Record<string, {
      interest?: string;
      startGrade?: string;
      endGrade?: string;
      description?: string;
      link?: string;
    }>;
    eventsAddedToCalendar?: Record<string, {
      eventId: string;
      calendarType: string;
      timestampAdded: number;
      userDeviceInfo: string;
      timestampCancelled?: number | null;
    }>;
  };
}

interface UserContextType {
  user: UserData | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug: Log when UserProvider mounts/unmounts
  useEffect(() => {
    console.log('🔄 UserProvider: Mounted');
    return () => {
      console.log('🔄 UserProvider: Unmounted');
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        console.log('🔄 UserContext: Setting up Firebase listener for user:', currentUser.uid);
        // Set up real-time listener for user data
        const db = getDatabase();
        const userRef = ref(db, `user/${currentUser.uid}`);
        
        // Set up real-time listener for guidance user data (for default filter updates)
        const guidanceUserRef = ref(db, `highSchoolUser/${currentUser.uid}`);
        let guidanceUserData: any = null;
        
        const guidanceUserUnsubscribe = onValue(guidanceUserRef, (guidanceSnapshot) => {
          if (guidanceSnapshot.exists()) {
            guidanceUserData = guidanceSnapshot.val();
            console.log('✅ UserContext: Guidance user data updated:', guidanceUserData);
            // Update user state with new guidance data if user already exists
            setUser(prevUser => {
              if (!prevUser) return prevUser;
              const defaultStudentFilter = guidanceUserData.defaultStudentFilter 
                ? {
                    lastNameRange: guidanceUserData.defaultStudentFilter.lastNameRange || undefined,
                    grades: guidanceUserData.defaultStudentFilter.grades || undefined,
                  }
                : undefined;
              return {
                ...prevUser,
                isGuidanceUser: true,
                schoolName: guidanceUserData.schoolName || prevUser.schoolName,
                schoolID: guidanceUserData.schoolID || prevUser.schoolID,
                userIcon: guidanceUserData.schoolIcon || prevUser.userIcon,
                email: guidanceUserData.account || prevUser.email,
                defaultStudentFilter: defaultStudentFilter,
              };
            });
          } else {
            guidanceUserData = null;
            // Update user state to remove guidance user flag if it was set
            setUser(prevUser => {
              if (!prevUser || !prevUser.isGuidanceUser) return prevUser;
              return {
                ...prevUser,
                isGuidanceUser: false,
                defaultStudentFilter: undefined,
              };
            });
          }
        }, (err) => {
          // If permission denied or doesn't exist, user is not a guidance user
          console.log('🔄 UserContext: User is not a guidance user or error checking:', err);
          guidanceUserData = null;
        });

        const userDataUnsubscribe = onValue(userRef, async (snapshot) => {
          // Re-read guidance user data to ensure we have the latest
          try {
            const guidanceUserSnapshot = await get(guidanceUserRef);
            if (guidanceUserSnapshot.exists()) {
              guidanceUserData = guidanceUserSnapshot.val();
              console.log('✅ UserContext: Found guidance user data:', guidanceUserData);
            } else {
              guidanceUserData = null;
            }
          } catch (err) {
            console.log('🔄 UserContext: Error checking guidance user data:', err);
            guidanceUserData = null;
          }
          try {
            
            setLoading(true);
            setError(null);

            if (snapshot.exists()) {
              const userData = snapshot.val();
              const profileData = userData.ProfileData || {};
              
              // Extract course data from the grades object (not ProfileData)
              const courses: { [courseCode: string]: { grade: string; grades: number; gradeType?: string } } = {};
              
              // Check if there's a grades object in userData
              if (userData.grades) {
                // Process each course in the grades object
                Object.entries(userData.grades).forEach(([courseCode, courseData]: [string, any]) => {
                  if (courseData && courseData.grade && courseData.grades) {
                    const gradeValue = parseFloat(courseData.grades.toString()) || 0;
                    courses[courseCode] = {
                      grade: courseData.grade.toString(), // This should now be the grade level (12, 11, 10)
                      grades: gradeValue,
                      gradeType: courseData.gradeType || 'actual' // Include grade type if available
                    };
                  }
                });
              }
              
              // Fallback: Try to extract from ProfileData if grades object doesn't exist
              if (Object.keys(courses).length === 0) {
                
                // Process Grade 11 courses - try multiple possible formats
                for (let i = 1; i <= 10; i++) {
                  // Try different possible field names
                  const courseName = profileData[`Grade 11 course ${i}`] || 
                                   profileData[`grade11course${i}`] || 
                                   profileData[`grade11_course_${i}`] ||
                                   profileData[`grade11Course${i}`];
                  const courseMark = profileData[`Grade 11 course ${i} mark`] || 
                                   profileData[`grade11course${i}mark`] || 
                                   profileData[`grade11_course_${i}_mark`] ||
                                   profileData[`grade11Course${i}Mark`] ||
                                   profileData[`grade11Course${i}Grade`];
                  
                  if (courseName && courseMark) {
                    const cleanCourseCode = extractCourseCode(courseName);
                    const gradeValue = parseFloat(courseMark.toString().replace('%', '')) || 0;
                    courses[cleanCourseCode] = {
                      grade: '11',
                      grades: gradeValue
                    };
                  }
                }
                
                // Process Grade 12 courses - try multiple possible formats
                for (let i = 1; i <= 10; i++) {
                  // Try different possible field names
                  const courseName = profileData[`Grade 12 course ${i}`] || 
                                   profileData[`grade12course${i}`] || 
                                   profileData[`grade12_course_${i}`] ||
                                   profileData[`grade12Course${i}`];
                  const courseMark = profileData[`Grade 12 course ${i} mark`] || 
                                   profileData[`grade12course${i}mark`] || 
                                   profileData[`grade12_course_${i}_mark`] ||
                                   profileData[`grade12Course${i}Mark`] ||
                                   profileData[`grade12Course${i}Grade`];
                  
                  if (courseName && courseMark) {
                    const cleanCourseCode = extractCourseCode(courseName);
                    const gradeValue = parseFloat(courseMark.toString().replace('%', '')) || 0;
                    courses[cleanCourseCode] = {
                      grade: '12',
                      grades: gradeValue
                    };
                  }
                }
                
                // Also try to look for course data in a different structure (like an array or object)
                // Check if there's a courses array or object
                if (profileData.courses) {
                  // Handle different course data structures
                  if (Array.isArray(profileData.courses)) {
                    profileData.courses.forEach((course: any, index: number) => {
                      if (course && course.code && course.grade) {
                        const gradeValue = parseFloat(course.actualGrade || course.grade || '0');
                        courses[course.code] = {
                          grade: course.gradeLevel || course.grade,
                          grades: gradeValue
                        };
                      }
                    });
                  } else if (typeof profileData.courses === 'object') {
                    Object.entries(profileData.courses).forEach(([key, course]: [string, any]) => {
                      if (course && course.code && course.grade) {
                        const gradeValue = parseFloat(course.actualGrade || course.grade || '0');
                        courses[course.code] = {
                          grade: course.gradeLevel || course.grade,
                          grades: gradeValue
                        };
                      }
                    });
                  }
                }
                
                // Check if course data might be stored directly in userData (not in ProfileData)
                if (userData.courses) {
                  if (Array.isArray(userData.courses)) {
                    userData.courses.forEach((course: any, index: number) => {
                      if (course && course.code && course.grade) {
                        const gradeValue = parseFloat(course.actualGrade || course.grade || '0');
                        courses[course.code] = {
                          grade: course.gradeLevel || course.grade,
                          grades: gradeValue
                        };
                      }
                    });
                  } else if (typeof userData.courses === 'object') {
                    Object.entries(userData.courses).forEach(([key, course]: [string, any]) => {
                      if (course && course.code && course.grade) {
                        const gradeValue = parseFloat(course.actualGrade || course.grade || '0');
                        courses[course.code] = {
                          grade: course.gradeLevel || course.grade,
                          grades: gradeValue
                        };
                      }
                    });
                  }
                }
              }
              
              
              // Extract grade averages from gradesAVG
              let grade12Average: number | undefined;
              let grade12Top6Average: number | undefined;
              
              if (userData.gradesAVG) {
                grade12Average = userData.gradesAVG.grade12AVG ? parseFloat(userData.gradesAVG.grade12AVG) : undefined;
                grade12Top6Average = userData.gradesAVG.grade12Top6AVG ? parseFloat(userData.gradesAVG.grade12Top6AVG) : undefined;
              }
              
              // Extract profileExtra data
              const profileExtra = userData.profileExtra || {};
              
              // If user is a guidance user, override school info and icon from highSchoolUser table
              const isGuidanceUser = !!guidanceUserData;
              const schoolName = isGuidanceUser ? (guidanceUserData.schoolName || '') : (profileData['High School'] || '');
              const schoolID = isGuidanceUser ? (guidanceUserData.schoolID || '') : (profileData.hsID || '');
              const userIcon = isGuidanceUser ? (guidanceUserData.schoolIcon || null) : (profileData.profileUrl || null);
              const email = isGuidanceUser && guidanceUserData.account ? (guidanceUserData.account || profileData.Email || '') : (profileData.Email || '');
              
              // Extract default student filter for guidance users
              const defaultStudentFilter = isGuidanceUser && guidanceUserData.defaultStudentFilter 
                ? {
                    lastNameRange: guidanceUserData.defaultStudentFilter.lastNameRange || undefined,
                    grades: guidanceUserData.defaultStudentFilter.grades || undefined,
                  }
                : undefined;
              
              const userInfo: UserData = {
                uid: currentUser.uid,
                email: email,
                username: `${profileData['First Name'] || ''} ${profileData['Last Name'] || ''}`.trim(),
                userIcon: userIcon,
                schoolName: schoolName,
                schoolID: schoolID,
                // Additional ProfileData fields
                city: profileData.City || '',
                classYear: profileData['Class Year'] || '',
                firstName: profileData['First Name'] || '',
                lastName: profileData['Last Name'] || '',
                phoneNumber: profileData['Phone Number'] || '',
                primaryLanguage: profileData['Primary Language'] || '',
                province: profileData.Province || '',
                status: profileData.Status || '',
                summary: profileData.Summary || '',
                gender: profileData.gender || '',
                hsBoard: profileData.hsBoard || '',
                registerStep: profileData.registerStep || '',
                isInternational: profileData.isInternational || false,
                country: profileData.Country || '',
                userType: profileData.isStudent || profileData.userType || undefined,
                isGuidanceUser: isGuidanceUser,
                selfClaimedGuidance: profileData.selfClaimedGuidance || false,
                defaultStudentFilter: defaultStudentFilter,
                // Social media URLs
                instagramUrl: profileData.instagramUrl || '',
                linkedinUrl: profileData.linkedinUrl || '',
                twitterUrl: profileData.twitterUrl || '',
                // account is the same as uid
                account: currentUser.uid,
                // Timestamp for when user data was last updated
                lastUpdated: userData.lastUpdated || undefined,
                // Course data
                courses,
                // Grade averages
                grade12Average,
                grade12Top6Average,
                // ProfileExtra data
                profileExtra,
              };
              
              setUser(userInfo);
            } else {
              // User exists in Auth but not in user table
              // Check if they're a guidance user
              const isGuidanceUser = !!guidanceUserData;
              const schoolName = isGuidanceUser ? (guidanceUserData.schoolName || '') : '';
              const schoolID = isGuidanceUser ? (guidanceUserData.schoolID || '') : '';
              const userIcon = isGuidanceUser ? (guidanceUserData.schoolIcon || null) : null;
              const email = isGuidanceUser && guidanceUserData.account ? guidanceUserData.account : (currentUser.email || '');
              
              // Extract default student filter for guidance users
              const defaultStudentFilter = isGuidanceUser && guidanceUserData.defaultStudentFilter 
                ? {
                    lastNameRange: guidanceUserData.defaultStudentFilter.lastNameRange || undefined,
                    grades: guidanceUserData.defaultStudentFilter.grades || undefined,
                  }
                : undefined;
              
              const userInfo: UserData = {
                uid: currentUser.uid,
                email: email,
                username: '',
                userIcon: userIcon,
                schoolName: schoolName,
                schoolID: schoolID,
                city: '',
                classYear: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                primaryLanguage: '',
                province: '',
                status: '',
                summary: '',
                gender: '',
                hsBoard: '',
                registerStep: '',
                isInternational: false,
                country: '',
                userType: undefined,
                isGuidanceUser: isGuidanceUser,
                defaultStudentFilter: defaultStudentFilter,
                // Social media URLs
                instagramUrl: '',
                linkedinUrl: '',
                twitterUrl: '',
                account: currentUser.uid,
                lastUpdated: undefined,
                courses: {},
                profileExtra: {},
              };
              setUser(userInfo);
            }
          } catch (err: any) {
            console.error('Error processing user data:', err);
            
            // Handle permission denied error gracefully
            if (err.code === 'PERMISSION_DENIED' || err.message?.includes('Permission denied')) {
              
              // Check if they're a guidance user even if user table access is denied
              const isGuidanceUser = !!guidanceUserData;
              const schoolName = isGuidanceUser ? (guidanceUserData.schoolName || '') : '';
              const schoolID = isGuidanceUser ? (guidanceUserData.schoolID || '') : '';
              const userIcon = isGuidanceUser ? (guidanceUserData.schoolIcon || null) : (currentUser.photoURL || null);
              const email = isGuidanceUser && guidanceUserData.account ? guidanceUserData.account : (currentUser.email || '');
              
              // Create a fallback user object with basic auth info
              const fallbackUser: UserData = {
                uid: currentUser.uid,
                email: email,
                username: currentUser.displayName || '',
                userIcon: userIcon,
                schoolName: schoolName,
                schoolID: schoolID,
                city: '',
                classYear: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                primaryLanguage: '',
                province: '',
                status: '',
                summary: '',
                gender: '',
                hsBoard: '',
                registerStep: '',
                isInternational: false,
                country: '',
                userType: undefined,
                isGuidanceUser: isGuidanceUser,
                // Social media URLs
                instagramUrl: '',
                linkedinUrl: '',
                twitterUrl: '',
                account: currentUser.uid,
                lastUpdated: undefined,
                courses: {},
                profileExtra: {},
              };
              setUser(fallbackUser);
              setError(null); // Don't show error for permission issues
            } else {
              setError('Failed to fetch user data');
            }
          } finally {
            setLoading(false);
          }
        }, (err) => {
          console.error('Error in user data listener:', err);
          
          // If it's a permission denied error, try to create the user record
          if (err.message && err.message.includes('permission_denied')) {
            
            // Create a basic user record for new users
            // Split the displayName to pre-populate first and last names
            const splitDisplayName = (fullName: string | null | undefined) => {
              if (!fullName || typeof fullName !== 'string' || fullName.trim() === '') {
                return { firstName: null, lastName: null };
              }
              
              const normalizedName = fullName.trim().replace(/\s+/g, ' ');
              const nameParts = normalizedName.split(' ');
              
              if (nameParts.length === 1) {
                return { firstName: nameParts[0], lastName: null };
              }
              
              if (nameParts.length === 2) {
                return { firstName: nameParts[0], lastName: nameParts[1] };
              }
              
              // Handle 3+ names - take first as first name, rest as last name
              const prefixes = ['Dr.', 'Dr', 'Mr.', 'Mr', 'Ms.', 'Ms', 'Mrs.', 'Mrs', 'Miss', 'Prof.', 'Prof'];
              let startIndex = 0;
              
              if (prefixes.includes(nameParts[0])) {
                startIndex = 1;
              }
              
              if (nameParts.length - startIndex >= 3) {
                return { 
                  firstName: nameParts[startIndex], 
                  lastName: nameParts.slice(startIndex + 1).join(' ') 
                };
              }
              
              if (nameParts.length - startIndex === 2) {
                return { 
                  firstName: nameParts[startIndex], 
                  lastName: nameParts[startIndex + 1] 
                };
              }
              
              return { firstName: nameParts[0], lastName: null };
            };
            
            const splitName = splitDisplayName(currentUser.displayName);
            
            const newUserData = {
              ProfileData: {
                Email: currentUser.email || '',
                // Pre-populate with split displayName, or null if no displayName
                'First Name': splitName.firstName,
                'Last Name': splitName.lastName,
                'High School': null,
                'Class Year': null,
                'Province': null,
                'Status': 'Active'
              },
              profileExtra: {},
              createdAt: Date.now()
            };
            
            // Try to set the user data
            import('firebase/database').then(({ set }) => {
              set(userRef, newUserData).then(() => {
                // The onValue listener will automatically pick up the new data
              }).catch((setErr) => {
                console.error('Failed to create user record:', setErr);
                setError('Failed to create user profile');
                setLoading(false);
              });
            });
          } else {
            setError('Failed to fetch user data');
            setLoading(false);
          }
        });

        // Return cleanup function for both listeners
        return () => {
          console.log('🔄 UserContext: Cleaning up Firebase listeners for user:', currentUser.uid);
          userDataUnsubscribe();
          guidanceUserUnsubscribe();
        };
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const refreshUser = () => {
    // The real-time listener will automatically update when data changes
    // This function is kept for backward compatibility
  };

  const value: UserContextType = {
    user,
    loading,
    error,
    refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 

// Helper function to extract course code from course name (matching your API logic)
function extractCourseCode(courseName: string): string {
  if (!courseName) return "";

  // If the course name contains "–" (en dash), split and take the first part
  if (courseName.includes("–")) {
    const parts = courseName.split("–").map((s) => s.trim());
    return parts[0];
  }

  // If the course name contains "-" (hyphen), split and take the first part
  if (courseName.includes("-")) {
    const parts = courseName.split("-").map((s) => s.trim());
    return parts[0];
  }

  // If no separator found, return the original string
  return courseName.trim();
} 